import { similarity } from "./import-merge";
import { normalizeForSearch } from "./slug";

// Deterministic language fixes for menu text read by the AI (owner, 2026-10-23: "AI-ul rezolvă singur
// singularul/pluralul, diacriticele evidente, majusculele, greșelile evidente; utilizatorul decide doar ce
// contează pentru corectitudinea meniului"). Nothing here invents content: it unifies letters, restores
// diacritics only when the word is unambiguous, and among spellings that readings gave, prefers real words.

/** Cyrillic and Greek letters that look like Latin ones (a model sometimes mixes them into a word: "IUТЕ"). */
const LOOKALIKE: Record<string, string> = {
  А: "A",
  В: "B",
  Е: "E",
  К: "K",
  М: "M",
  Н: "H",
  О: "O",
  Р: "P",
  С: "C",
  Т: "T",
  Х: "X",
  У: "Y",
  І: "I",
  а: "a",
  е: "e",
  о: "o",
  р: "p",
  с: "c",
  х: "x",
  у: "y",
  і: "i",
  к: "k",
  м: "m",
  т: "t",
  н: "h",
  Α: "A",
  Β: "B",
  Ε: "E",
  Ζ: "Z",
  Η: "H",
  Ι: "I",
  Κ: "K",
  Μ: "M",
  Ν: "N",
  Ο: "O",
  Ρ: "P",
  Τ: "T",
  Υ: "Y",
  Χ: "X",
  ο: "o",
  ν: "v",
};

/** Latin letters only, Romanian comma-below letters (ș ț, not the cedilla ş ţ), single spaces. */
export function cleanText(s: string): string {
  return s
    .replace(/[Ͱ-ϿЀ-ӿ]/g, (ch) => LOOKALIKE[ch] ?? ch)
    .replace(/ş/g, "ș")
    .replace(/Ş/g, "Ș")
    .replace(/ţ/g, "ț")
    .replace(/Ţ/g, "Ț")
    .replace(/[  -​]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Words that menus in Romania use (Romanian dishes and ingredients with their usual forms, and the Italian,
// French and English names printed on Romanian menus). General vocabulary, not taken from any one menu.
const WORDS = `
a al ale alb albă albe alune aluat ananas ancora andive anghinare ardei ardeiul aripioare aripi aromat aromată
aromate arpagic arțar avocado afumat afumată afumate bacon banane banană baton batoane bere bezea biftec biscuit
biscuiți boia brânză brânzeturi brioșă broccoli bruschete bruschetă bucată bucăți budincă bulion burger burgeri
burtă busuioc cafea calamar calamari cald caldă calde capere capră caramel caramelizat caramelizată caramelizate
carne carnea cartof cartofi cartofii cașcaval castravete castraveți caș ceafă ceapă ceai cereale cheesecake chiftele
chili chimen ciocolată ciorbă ciorbe ciuperci ciupercile clătite clătită coacăze coapse coaste cocktail cocktailuri
cocos cod copt coaptă coapte coriandru costiță cotlet cotlete crap cremă creme cremos cremoasă crevete creveți
crocant crocantă crocante crudități crud cruton crutoane cu curcan curry cușcuș cârnați cârnat de deasupra delicios
desert deserturi din dovlecel dovlecei dressing dulce dulceață dulci după espresso fasole felie felii ficat ficăței
file fileu fileuri fistic fără fiert fiartă fierte foietaj fructe fructul fulgi fum gălbenuș garnitură garnituri
gem ghimbir gin grătar gratinat gratinată gratinate grâu gulaș hamsii hrean humus iaurt iute iuți în înghețată
ierburi jambon julien la lapte legume lămâie lămâi limonadă linte lipie lichior măsline mălai mămăligă măr mere
mărar mazăre mic mici mic-dejun miel miere migdale mix mozzarella mușchi muștar mure nuci nucă ochiuri omletă
orez oregano ou ouă pâine pane papanași paste pastă pătrunjel pecorino pârjoale pe pește pește pești piept
piersici pită pizza pizze piure plăcintă platou porc porție porții praz prăjit prăjită prăjiți prăjite prosciutto
pui pulpă pulpe ragu răcoritoare rață rasol roșie roșii rozmarin salam salată salate sardine sarmale sec seacă
sfeclă semințe smântână șnițel somon sos sosuri spanac sparanghel specialitate specialități spumă stafide
steak sticlă sticle suc sucuri supă supe susan șuncă tăiței telemea tartă tarte tartar tarhon ton topping trufe
trufă tocăniță tochitură tomate tort turte unt usturoi vânătă vinete vin vinuri vită vișine zacuscă zahăr zmeură
alb negru roșu roz rosé verde verzi galben galbeni proaspăt proaspătă proaspete natural naturală naturale
casei casă tradițional tradițională tradiționale picant picantă picante dulce acru acrișor sărat sărată
mare mari mică mici medie mediu întreg întreagă jumătate
ardelenesc ardelenească moldovenesc moldovenească bănățean bucovinean țărănesc țărănească ciobănesc ciobănească
mititei micii frigărui frigăruie cotlet pastramă tobă caltaboș lebăr drob sarmaluțe mâncare tocană
afine căpșuni cireșe vișine prune caise struguri portocale portocală grapefruit kiwi mango fructul pepene
lămâiță mentă busuioc cimbru leuștean dafin piper sare zahăr scorțișoară vanilie cardamom
apă plată minerală carbogazoasă tonic cola ceai cafea ciocolată caldă frappe frappé latte cappuccino
macchiato americano ristretto lungo decaf flat white irish
vodka whisky whiskey rom romul tequila lichior coniac vinars țuică pălincă amaro aperitiv digestiv shot
șampanie prosecco spumant cidru bere brută nefiltrată blondă brună neagră draft draught halbă
pahar pahare sticlă carafă
caesar greek greacă bulgărească orientală boeuf cobb niçoise
pasta penne spaghete spaghetti tagliatelle fettuccine fettucine linguine rigatoni ravioli gnocchi lasagna lasagne
carbonara bolognese arrabbiata amatriciana pesto alfredo aglio olio peperoncino puttanesca
risotto bruschetta focaccia foccacia ciabatta calzone margherita margarita capricciosa diavola quattro
stagioni formaggi funghi prosciutto cotto crudo salami salame mortadella pistacchio bresaola burrata
parmezan parmigiano grana gorgonzola ricotta mascarpone pecorino taleggio provola scamorza feta halloumi haloumi
tiramisu panna cotta cannoli profiterol eclair clafoutis crème brûlée sufleu souffle fondant lava
clătite pancakes waffle vafe gofre croissant croasant brioșă briose muffin brownie cookie
toast sandvici sandviș sandwich wrap tortilla quesadilla nachos tacos burrito
burger cheeseburger hamburger chicken beef pork bacon cheese fries chips onion rings
scrambled eggs benedict omelette shakshuka granola porridge ovăz chia
steak ribeye antricot vrăbioară mușchiuleț t-bone tomahawk striploin entrecote
somon păstrăv dorada lup biban ton cod calcan creveți scoici midii caracatiță calamar sepie
fructe de mare
vegan vegetarian vegetariană fără gluten lactoză
side dishes extra extras sauce sosuri ketchup maioneză barbeque muștar usturoi
cu de și la în din pe fără sau ori pentru peste sub între lângă prin al a ale ai
`;

const LEXICON = new Map<string, string>(); // normalized → word as written (with diacritics)
/** Forms written two ways ("frappe" / "frappé"): their diacritics are never restored. */
const AMBIGUOUS = new Set<string>();
for (const w of WORDS.split(/\s+/).filter(Boolean)) {
  const k = normalizeForSearch(w);
  const known = LEXICON.get(k);
  if (known === undefined) LEXICON.set(k, w);
  else if (known !== w) AMBIGUOUS.add(k);
}

export const isKnownWord = (word: string) => LEXICON.has(normalizeForSearch(word));

/** Writes `word` in the letter case of `like` (MENIU, Meniu, meniu). */
function inCaseOf(like: string, word: string): string {
  if (like === like.toUpperCase() && like !== like.toLowerCase()) return word.toUpperCase();
  if (like[0] && like[0] === like[0].toUpperCase()) return word[0]?.toUpperCase() + word.slice(1);
  return word;
}

/**
 * One word made right when it is obviously the same word: diacritics restored when the menu word is known and
 * unambiguous ("SUNCA" → "ȘUNCĂ"). Unknown words (brands, names) stay as read.
 */
export function fixWord(word: string): { word: string; fixed: boolean } {
  const letters = word.replace(/[^\p{L}]/gu, "");
  if (letters.length < 2) return { word, fixed: false };
  const k = normalizeForSearch(letters);
  const known = LEXICON.get(k);
  // No "nearest known word" guess: valid words outside the list would be changed ("AMERICAN" → "AMERICANO").
  // A misread word is settled by the other readings instead (knownShare in the choice of spelling).
  if (known === undefined || AMBIGUOUS.has(k) || letters === inCaseOf(letters, known))
    return { word, fixed: false };
  // Same letters: restore the diacritics the reading dropped or misplaced.
  return { word: word.replace(letters, inCaseOf(letters, known)), fixed: true };
}

/** A name with its obvious word fixes applied, and whether anything changed. */
export function fixName(name: string): { name: string; fixed: boolean } {
  let fixed = false;
  const out = cleanText(name)
    .split(" ")
    .map((w) => {
      const r = fixWord(w);
      fixed ||= r.fixed;
      return r.word;
    })
    .join(" ");
  return { name: out, fixed: fixed || out !== name };
}

/** Share of a spelling's words (3+ letters) that are known menu words: real words beat misreadings. */
export function knownShare(name: string): number {
  const words = cleanText(name)
    .split(" ")
    .map((w) => w.replace(/[^\p{L}]/gu, ""))
    .filter((w) => w.length >= 3);
  if (!words.length) return 0;
  return words.filter(isKnownWord).length / words.length;
}

// Romanian / English plural and article endings, longest first: "PIZZE" ~ "PIZZA", "SALATE" ~ "SALATĂ",
// "DESERTURI" ~ "DESERT", "BURGERS" ~ "BURGER".
const ENDINGS = ["urile", "ului", "uri", "ele", "ile", "lor", "le", "ii", "ul", "uo", "i", "e", "a", "s"];

/** A word without its inflection ending (for comparing, never for display). */
export function stem(word: string): string {
  const k = normalizeForSearch(word);
  if (k.length <= 4) return k;
  for (const e of ENDINGS) if (k.endsWith(e) && k.length - e.length >= 4) return k.slice(0, -e.length);
  return k;
}

/** Comparison key that ignores letter case, diacritics, look-alike letters, punctuation and inflection endings. */
export function looseKey(s: string): string {
  return cleanText(s)
    .split(/[^\p{L}\p{N}%]+/u)
    .filter(Boolean)
    .map(stem)
    .join(" ");
}

/**
 * Two readings of the same printed name: identical apart from case, diacritics, look-alike letters, singular /
 * plural or one misread letter in a long name. Different dishes ("Pizza Diavola" / "Pizza Capricciosa") are not.
 */
export function sameName(a: string, b: string): boolean {
  const x = looseKey(a);
  const y = looseKey(b);
  if (x === y) return true;
  if (!x || !y) return false;
  const s = similarity(x, y);
  return s >= 0.9 || (Math.min(x.length, y.length) >= 12 && s >= 0.85);
}
